import React, { useState, useEffect } from 'react';
import { BriefcaseBusiness, Calendar, SquarePen, UserRound } from 'lucide-react';
import EditProfileModal from './components/EditProfileModal';
import { getCurrentUserProfile, getUserActivities } from './api/profileApi';
import api from '../../api';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [imageRefreshKey, setImageRefreshKey] = useState(0);
  const [profileEvents, setProfileEvents] = useState([]);

  // Load user profile data + reference options + activities
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [profileRes, deptRes, jobRes, rolesRes, activitiesRes] = await Promise.all([
          getCurrentUserProfile(),
          api.get('/md/departement'),
          api.get('/md/job-title'),
          api.get('/auth/role-options'),
          getUserActivities(0, 10) // Get first 10 activities
        ]);

        setUserData(profileRes);
        setDepartments(deptRes?.data || deptRes || []);
        setJobTitles(jobRes?.data || jobRes || []);
        setRoleOptions(rolesRes?.data || rolesRes || []);
        
        // Format activities for display
        if (activitiesRes?.activities) {
          const formattedActivities = activitiesRes.activities.map(activity => ({
            subject: activity.subject,
            date: formatDateForDisplay(activity.date),
            time: activity.time,
            description: activity.description,
            icon: activity.icon
          }));
          setProfileEvents(formattedActivities);
        }
      } catch (error) {
        console.error('Error loading profile and master data:', error);
        // Set default activities if API fails
        setProfileEvents([
          {
            subject: "Welcome!",
            date: "Today",
            time: "00:00",
            description: "You joined the platform",
            icon: "user",
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleEditProfile = () => {
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
  };

  const handleEditSuccess = () => {
    // Reload user data and activities after successful update
    const fetchUserData = async () => {
      try {
        console.log('=== PROFILE UPDATE DEBUG ===');
        console.log('1. Refreshing user profile data and activities after edit...');
        console.log('2. Current userData before refresh:', userData);
        
        // Fetch fresh data from the same endpoint used for updates
        const [profileData, activitiesData] = await Promise.all([
          getCurrentUserProfile(),
          getUserActivities(0, 10)
        ]);
        
        console.log('3. Fresh profile data from API:', profileData);
        console.log('4. ProfilePicture filename (photo):', profileData?.photo);
        console.log('4. ProfilePicture filename (profilePicture):', profileData?.profilePicture);
        console.log('5. Signature filename:', profileData?.signature);
        console.log('6. Created date:', profileData?.createdAt);
        console.log('7. Fresh activities data:', activitiesData);
        
        // Force immediate update with new timestamp
        const newTimestamp = Date.now();
        console.log('8. Setting refreshTimestamp:', newTimestamp);
        
        // Update userData with fresh data and timestamp
        setUserData(prevData => {
          const newData = {
            ...prevData,
            ...profileData,
            refreshTimestamp: newTimestamp
          };
          console.log('9. New userData after update:', newData);
          return newData;
        });
        
        // Update activities
        if (activitiesData?.activities) {
          const formattedActivities = activitiesData.activities.map(activity => ({
            subject: activity.subject,
            date: formatDateForDisplay(activity.date),
            time: activity.time,
            description: activity.description,
            icon: activity.icon
          }));
          setProfileEvents(formattedActivities);
          console.log('10. Updated activities:', formattedActivities);
        }
        
        // Force a second update after a short delay
        setTimeout(() => {
          console.log('11. Force second refresh...');
          setUserData(prevData => ({
            ...prevData,
            forceRefresh: Date.now()
          }));
          
          // Force image re-render with new key
          setImageRefreshKey(prev => prev + 1);
          console.log('12. Image refresh key updated to:', imageRefreshKey + 1);
        }, 200);
        
        console.log('=== END PROFILE UPDATE DEBUG ===');
        
      } catch (error) {
        console.error('Error fetching updated user profile:', error);
      }
    };
    
    // Add a small delay to ensure backend has processed the update
    setTimeout(fetchUserData, 500);
  };

  // Helpers to resolve names like in InternalUsers.js
  const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'N/A';
    const found = departments.find(d => (d.id == departmentId));
    return found ? (found.name || 'N/A') : 'N/A';
  };

  const getJobTitleName = (jobTitleId) => {
    if (!jobTitleId) return 'N/A';
    const found = jobTitles.find(jt => (jt.id == jobTitleId));
    return found ? (found.name || 'N/A') : 'N/A';
  };

  const getPrimaryRoleLabel = (roles) => {
    // userData.roles comes as array of { id, name, description }
    if (Array.isArray(roles) && roles.length > 0) {
      // Try to map to roleOptions to get human label
      const roleNames = roles.map(r => (r.name || r)).filter(Boolean);
      const labels = roleOptions
        .filter(ro => roleNames.includes(ro.value) || roleNames.includes(ro.label) || roleNames.includes(ro.name))
        .map(ro => ro.label || ro.name || ro.value);
      if (labels.length > 0) return labels[0];
      return (roles[0].name || roles[0]).toString();
    }
    return 'N/A';
  };

  // Function to format joined date
  const formatJoinedDate = (createdAt) => {
    if (!createdAt) return 'Joined Unknown';
    
    try {
      const date = new Date(createdAt);
      const options = { year: 'numeric', month: 'long' };
      const formattedDate = date.toLocaleDateString('en-US', options);
      return `Joined ${formattedDate}`;
    } catch (error) {
      console.error('Error formatting joined date:', error);
      return 'Joined Unknown';
    }
  };

  // Function to format date for activity display
  const formatDateForDisplay = (dateString) => {
    try {
      const date = new Date(dateString);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
    } catch (error) {
      console.error('Error formatting activity date:', error);
      return dateString;
    }
  };

  // Helper function to get photo URL (following InternalUsers.js pattern)
  const getPhotoUrl = (photoFileName) => {
    if (!photoFileName) return null;
    const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8080';
    // Use multiple cache busting parameters for maximum refresh
    const refreshTimestamp = userData?.refreshTimestamp || Date.now();
    const forceRefresh = userData?.forceRefresh || Date.now();
    const currentTime = Date.now();
    console.log('getPhotoUrl called with:', {
      photoFileName,
      refreshTimestamp,
      forceRefresh,
      currentTime
    });
    return `${apiUrl}/api/public/displayimage/${photoFileName}?t=${refreshTimestamp}&f=${forceRefresh}&c=${currentTime}`;
  };

  // Helper function to get signature URL (following InternalUsers.js pattern)  
  const getSignatureUrl = (signatureFileName) => {
    if (!signatureFileName) return null;
    const apiUrl = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8080';
    // Use multiple cache busting parameters for maximum refresh
    const refreshTimestamp = userData?.refreshTimestamp || Date.now();
    const forceRefresh = userData?.forceRefresh || Date.now();
    const currentTime = Date.now();
    console.log('getSignatureUrl called with:', {
      signatureFileName,
      refreshTimestamp,
      forceRefresh,
      currentTime
    });
    return `${apiUrl}/api/public/displayimage/${signatureFileName}?t=${refreshTimestamp}&f=${forceRefresh}&c=${currentTime}`;
  };

  const profileImageUrl = getPhotoUrl(userData?.photo || userData?.profilePicture);

  // Debug: Log the final URL being used
  console.log('=== IMAGE URL DEBUG ===');
  console.log('userData.photo:', userData?.photo);
  console.log('userData.profilePicture:', userData?.profilePicture);
  console.log('Final profileImageUrl:', profileImageUrl);
  console.log('=== END IMAGE URL DEBUG ===');

  if (loading || !userData) {
    return <div className="text-center p-4 text-gray-500">Loading...</div>;
  }

  return (
    <div className="container-xl">
      <div className="w-100 mb-3">
        {/* Background banner */}
        <div
          className="w-100 rounded-top"
          style={{
            height: "160px",
            background: "linear-gradient(120deg, #27a0a6 65%, #d8e04c 35%)",
          }}
        ></div>

        {/* Profile section */}
        <div className="bg-white shadow-sm d-flex justify-content-between align-items-center p-3 position-relative rounded-bottom">
          {/* Left section */}
          <div className="d-flex align-items-center">
            {/* Profile image */}
            {profileImageUrl ? (
              <img
                key={`profile-${imageRefreshKey}-${userData?.refreshTimestamp || userData?.forceRefresh || Date.now()}`}
                src={profileImageUrl}
                alt="Profile"
                className="rounded border border-white shadow-lg"
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  marginTop: "-60px",
                }}
                onLoad={() => console.log('Profile image loaded successfully:', profileImageUrl)}
                onError={(e) => console.error('Profile image failed to load:', profileImageUrl, e)}
              />
            ) : (
              <span className="avatar avatar-xl mb-0 rounded border border-white shadow-lg d-flex align-items-center justify-content-center"
                style={{ width: '80px', height: '80px', marginTop: '-60px' }}>
                {(userData?.firstName?.[0] || '')}{(userData?.lastName?.[0] || '')}
              </span>
            )}

            {/* Name + info */}
            <div className="ms-3">
              <div className="mb-1 h1">{`${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.username}</div>
              <div className="text-muted small d-flex gap-3">
                <div className="h4 d-flex align-items-center">
                  <BriefcaseBusiness className="icon icon-tabler text-muted me-2" />
                  {userData?.jobTitle || getJobTitleName(userData?.jobTitleId)}
                </div>
                <div className="h4 d-flex align-items-center">
                  <Calendar className="icon icon-tabler text-muted me-2" />
                  {formatJoinedDate(userData?.createdAt)}
                </div>
              </div>
            </div>
          </div>

          {/* Right section - Edit Button */}
          <button className="btn btn-primary d-flex align-items-center" onClick={handleEditProfile}>
            <SquarePen className="icon icon-tabler me-2" />
            Edit Profile
          </button>
        </div>
      </div>

      {/* <nav class="nav nav-segmented nav-2" role="tablist">
        <button class="nav-link active" role="tab" data-bs-toggle="tab" aria-selected="true" aria-current="page" fdprocessedid="u0t0z7">
          <UserRound className="icon icon-tabler text-muted me-2" />
          Profile
        </button>
        <button class="nav-link" role="tab" data-bs-toggle="tab" aria-selected="false" tabindex="-1">
          <BriefcaseBusiness className="icon icon-tabler text-muted me-2" />
          Project
        </button>
      </nav> */}

      <div className="row">
        <div className="col-md-4">
          <div className="card mb-3">
            <div className="card-header">
              <div class="h3 card-title">Base info</div>
            </div>
            <div className="card-body">
              <div className="datagrid">
                <div className="datagrid-item">
                  <div className="datagrid-title">User Name</div>
                  <div className="datagrid-content">{userData?.username || 'N/A'}</div>
                </div>
                <div className="datagrid-item">
                  <div className="datagrid-title">Status</div>
                  <div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${userData?.status === "1" || userData?.status === "active"
                        ? "badge bg-green-lt"
                        : "badge bg-default-lt"
                        }`}
                    >
                      {userData?.status === '1' ? 'active' : (userData?.status || 'inactive')}
                    </span>
                  </div>
                </div>
                <div className="datagrid-item">
                  <div className="datagrid-title">Job Title</div>
                  <div className="datagrid-content">{userData?.jobTitle || getJobTitleName(userData?.jobTitleId)}</div>
                </div>
                <div className="datagrid-item">
                  <div className="datagrid-title">Department</div>
                  <div className="datagrid-content">{getDepartmentName(userData?.departmentId) || userData?.department || 'N/A'}</div>
                </div>
                <div className="datagrid-item">
                  <div className="datagrid-title">Phone</div>
                  <div className="datagrid-content">{userData?.nohp || userData?.phone || 'N/A'}</div>
                </div>
                <div className="datagrid-item">
                  <div className="datagrid-title">Email</div>
                  <div className="datagrid-content">{userData?.email || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <div class="h3 card-title">Overview</div>
            </div>
            <div className="card-body">
              <div className="datagrid">
                <div className="datagrid-item">
                  <div class="datagrid-title">Task Completed</div>
                  <div class="datagrid-content">{userData?.tasksCompleted || '0'}</div>
                </div>
                <div className="datagrid-item">
                  <div class="datagrid-title">Project Completed</div>
                  <div class="datagrid-content">{userData?.projectsCompleted || '0'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <div class="h3 card-title">Activity Timeline</div>
            </div>
            <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <div className="activity-timeline">
                {profileEvents.map((event, index) => (
                  <div key={index} className="activity-item">
                    <div className="row g-0">
                      <div className="col-auto position-relative">
                        <div className="activity-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: '24px',
                            height: '24px',
                            position: 'relative',
                            zIndex: 2
                          }}>
                          {getActivityIcon(event.icon)}
                        </div>
                      </div>
                      <div className="col ms-3">
                        <div>
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <div className="activity-title text-primary me-2">
                              {event.subject}
                            </div>
                            <div className="activity-time text-muted small">
                              {event.date} {event.time}
                            </div>
                          </div>
                          <div className="activity-description text-muted">
                            <span className="fw-bold">you</span> {event.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

const getActivityIcon = (iconType) => {
  switch (iconType) {
    case 'user':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-user" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"></path>
          <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
        </svg>
      );
    case 'lock':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-lock" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6z"></path>
          <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0"></path>
          <path d="M8 11v-4a4 4 0 1 1 8 0v4"></path>
        </svg>
      );
    case 'users':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-users" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path>
          <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"></path>
        </svg>
      );
    case 'message':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-message" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M8 9h8"></path>
          <path d="M8 13h6"></path>
          <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12z"></path>
        </svg>
      );
    default:
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-activity" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
          <path d="M3 12h4l3 8l4 -16l3 8h4"></path>
        </svg>
      );
  }
};

export default Profile; 
